/**
 * BIỂU ĐỒ THU / CHI THEO NGÀY (sổ thu/chi)
 *
 * Cột phân cực quanh vạch 0: THU dựng lên, CHI dựng xuống. Chọn dạng này vì chiều cột
 * đã nói thu hay chi — không phải dò màu — và nhìn một cái là thấy ngày nào âm.
 * HAI CHIỀU DÙNG CHUNG MỘT THANG (cùng số đồng trên mỗi pixel), không phải hai trục.
 *
 * Màu: xanh #1FBF86 (thu) / đỏ #D6273A (chi). Lệch nhẹ so với --green/--red của app là
 * CÓ CHỦ Ý: cặp màu cũ (#0EA96B ↔ #EF4655) chỉ cách nhau ΔE 4.3 với người mù màu đỏ-lục,
 * gần như một màu; cặp này đạt 14.1 (kiểm bằng validator của skill dataviz). Vẫn giữ
 * xanh=thu / đỏ=chi cho khớp thẻ tổng và bảng giao dịch ở cùng trang.
 *
 * Độ tương phản của xanh trên nền trắng là 2.31 (<3:1) — bù bằng nhãn trực tiếp ở cột
 * lớn nhất, chú giải, và BẢNG giao dịch ngay dưới chứa đủ mọi con số.
 *
 * App chỉ có một tông sáng (không có dark mode) nên không cần bậc màu cho nền tối.
 *
 * Khoảng ngày dài thì gom nhóm: <= 92 ngày vẽ theo NGÀY, <= 730 theo TUẦN, dài hơn theo
 * THÁNG — cột mảnh hơn 3-4px là không đọc được nữa, gom lại trung thực hơn là vẽ cho có.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

const THU = '#1FBF86';
const CHI = '#D6273A';

const PAD = { l: 56, r: 14, t: 20, b: 32 };
const PLOT_H = 190;
const MAX_BAR = 24; // theo spec: cột không bao giờ lấp đầy ô, phần thừa để làm khoảng thở

const ymd = (d) => d.toISOString().slice(0, 10);
const parseYmd = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const addDays = (d, n) => new Date(d.getTime() + n * 86400000);

// Tiền rút gọn cho nhãn trục: 10.000.000 -> "10 tr". Số đầy đủ nằm ở tooltip và bảng.
function shortVnd(n) {
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1e9) return sign + (a / 1e9).toFixed(a >= 1e10 ? 0 : 1).replace('.', ',') + ' tỷ';
  if (a >= 1e6) return sign + (a / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace('.', ',') + ' tr';
  if (a >= 1e3) return sign + Math.round(a / 1e3) + ' ng';
  return sign + a;
}
const fullVnd = (n) => (Number(n) || 0).toLocaleString('vi-VN') + ' ₫';

// Bước chia trục "đẹp": 1 / 2 / 5 × 10^k.
function niceStep(raw) {
  if (!(raw > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const r = raw / p;
  return (r <= 1 ? 1 : r <= 2 ? 2 : r <= 5 ? 5 : 10) * p;
}

// Cột bo 4px ở ĐẦU SỐ LIỆU, vuông ở chân vạch 0 (spec marks-and-anatomy).
function barPath(x, y, w, h, up) {
  const r = Math.min(4, w / 2, h);
  if (h <= 0) return '';
  return up
    ? `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`
    : `M${x},${y} L${x},${y + h - r} Q${x},${y + h} ${x + r},${y + h} L${x + w - r},${y + h} Q${x + w},${y + h} ${x + w},${y + h - r} L${x + w},${y} Z`;
}

// Bề rộng thật của khung — cột co giãn theo màn hình thay vì chọn bừa một con số.
function useWidth(ref) {
  const [w, setW] = useState(880);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setW(Math.max(320, el.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

// Gom giao dịch về từng mốc thời gian trong khoảng đang lọc. Ngày KHÔNG có giao dịch
// vẫn giữ chỗ — bỏ đi thì trục thời gian co lại và biểu đồ nói dối về nhịp thu/chi.
function buildBuckets(txns, from, to) {
  const dates = txns.map(t => t.date).filter(Boolean).sort();
  const start = parseYmd(from || dates[0] || ymd(new Date()));
  const end = parseYmd(to || dates[dates.length - 1] || ymd(new Date()));
  if (isNaN(start) || isNaN(end) || end < start) return { list: [], unit: 'day' };

  const days = Math.round((end - start) / 86400000) + 1;
  const unit = days <= 92 ? 'day' : days <= 730 ? 'week' : 'month';
  const keyOf = (d) => {
    if (unit === 'day') return ymd(d);
    if (unit === 'week') return ymd(addDays(d, -((d.getUTCDay() + 6) % 7))); // về thứ Hai
    return ymd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
  };

  const map = new Map();
  for (let d = start; d <= end; d = addDays(d, 1)) {
    const k = keyOf(d);
    if (!map.has(k)) map.set(k, { key: k, thu: 0, chi: 0, n: 0 });
  }
  for (const t of txns) {
    if (!t.date) continue;
    const d = parseYmd(t.date);
    if (d < start || d > end) continue;
    const b = map.get(keyOf(d));
    if (!b) continue;
    if (t.kind === 'thu') b.thu += t.amount || 0;
    else b.chi += t.amount || 0;
    b.n++;
  }
  return { list: [...map.values()], unit };
}

const UNIT_LABEL = { day: 'ngày', week: 'tuần', month: 'tháng' };

export default function LedgerChart({ rep, from, to }) {
  const wrapRef = useRef(null);
  const width = useWidth(wrapRef);
  const [hover, setHover] = useState(null);

  const { list, unit } = useMemo(
    () => buildBuckets((rep && rep.txns) || [], from, to),
    [rep, from, to],
  );

  const n = list.length;
  const plotW = Math.max(60, width - PAD.l - PAD.r);
  const slot = n ? plotW / n : plotW;
  const barW = Math.max(2, Math.min(MAX_BAR, slot - 2)); // 2px khe giữa hai cột kề nhau

  const maxThu = Math.max(0, ...list.map(b => b.thu));
  const maxChi = Math.max(0, ...list.map(b => b.chi));
  const hasData = maxThu > 0 || maxChi > 0;

  // MỘT thang duy nhất cho cả hai chiều (cùng số đồng trên mỗi pixel), chia theo đúng
  // giá trị lớn nhất + 12% chỗ trống để nhãn trực tiếp không đụng mép. Làm tròn cột lên
  // bội số của bước chia thì cột 10,04tr phải chịu trục tới 15tr, mất một phần ba chiều
  // cao vào khoảng trắng.
  const HEAD = 1.12;
  const upSpan = maxThu * HEAD;
  const downSpan = maxChi * HEAD;
  const span = Math.max(upSpan + downSpan, 1);
  const perUnit = PLOT_H / span; // pixel trên mỗi đồng
  const zeroY = PAD.t + upSpan * perUnit;
  const H = PAD.t + PLOT_H + PAD.b;
  // Vạch lưới ở bội số "đẹp" của bước chia, chỉ vẽ những vạch nằm trong khung.
  const step = niceStep(Math.max(maxThu, maxChi) / 2 || 1);

  // Nhãn trực tiếp: CHỈ cột thu lớn nhất và cột chi lớn nhất (không dán số lên mọi cột).
  const iMaxThu = maxThu > 0 ? list.findIndex(b => b.thu === maxThu) : -1;
  const iMaxChi = maxChi > 0 ? list.findIndex(b => b.chi === maxChi) : -1;

  const labelOf = (b) => {
    const d = parseYmd(b.key);
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return unit === 'month' ? `${mm}/${d.getUTCFullYear()}` : `${dd}/${mm}`;
  };
  // Chỉ in nhãn trục X khi còn đủ chỗ, tránh chữ chồng lên nhau.
  const everyX = Math.max(1, Math.ceil(44 / slot));

  const ticks = [0];
  for (let v = step; v <= upSpan; v += step) ticks.push(v);
  for (let v = step; v <= downSpan; v += step) ticks.push(-v);

  // Nhãn cực trị ở sát mép thì kéo vào trong, không để chữ tràn ra ngoài khung.
  const clampLabelX = (x) => Math.min(Math.max(x, PAD.l + 18), width - PAD.r - 18);

  const hb = hover != null ? list[hover] : null;

  return (
    <div className="lc" ref={wrapRef}>
      <div className="lc-head">
        <div>
          <div className="lc-title">Thu / chi theo {UNIT_LABEL[unit]}</div>
          <div className="lc-sub">
            {rep && rep.q ? `Đang lọc: “${rep.q}” · ` : ''}
            {n} {UNIT_LABEL[unit]} trong khoảng đang xem
          </div>
        </div>
        <div className="lc-legend">
          <span><i style={{ background: THU }} />Thu</span>
          <span><i style={{ background: CHI }} />Chi</span>
        </div>
      </div>

      {!hasData ? (
        <div className="lc-empty">Không có giao dịch trong khoảng này</div>
      ) : (
        <div className="lc-plot">
          <svg width="100%" height={H} viewBox={`0 0 ${width} ${H}`} role="img"
            aria-label={`Biểu đồ thu chi theo ${UNIT_LABEL[unit]}. Tổng thu ${fullVnd(rep.total_in)}, tổng chi ${fullVnd(rep.total_out)}. Số liệu chi tiết có ở bảng giao dịch bên dưới.`}
            onMouseLeave={() => setHover(null)}>
            {/* Lưới: nét liền 1px, lùi hẳn về sau */}
            {ticks.map(v => {
              const y = zeroY - v * perUnit;
              return (
                <g key={v}>
                  <line x1={PAD.l} y1={y} x2={width - PAD.r} y2={y}
                    stroke={v === 0 ? 'var(--border2)' : 'var(--border)'} strokeWidth="1" />
                  <text x={PAD.l - 8} y={y + 4} textAnchor="end" className="lc-axis">{shortVnd(v)}</text>
                </g>
              );
            })}

            {list.map((b, i) => {
              const x = PAD.l + i * slot;
              const bx = x + (slot - barW) / 2;
              // Tối thiểu 2px: một khoản thu/chi có thật không được biến mất khỏi biểu đồ.
              const hThu = b.thu > 0 ? Math.max(2, b.thu * perUnit) : 0;
              const hChi = b.chi > 0 ? Math.max(2, b.chi * perUnit) : 0;
              return (
                <g key={b.key}>
                  {hover === i && <rect x={x} y={PAD.t} width={slot} height={PLOT_H} fill="var(--bg4)" opacity="0.7" />}
                  {b.thu > 0 && <path d={barPath(bx, zeroY - hThu, barW, hThu, true)} fill={THU} />}
                  {b.chi > 0 && <path d={barPath(bx, zeroY, barW, hChi, false)} fill={CHI} />}
                  {/* Chỉ hai nhãn: cột thu cao nhất và cột chi sâu nhất. Vẽ cả khi cột
                      hẹp — hai chữ số không gây rối, mà đây là chỗ bù cho việc màu xanh
                      hơi nhạt so với nền. */}
                  {i === iMaxThu && (
                    <text x={clampLabelX(bx + barW / 2)} y={zeroY - hThu - 6} textAnchor="middle" className="lc-val">{shortVnd(b.thu)}</text>
                  )}
                  {i === iMaxChi && (
                    <text x={clampLabelX(bx + barW / 2)} y={zeroY + hChi + 13} textAnchor="middle" className="lc-val">{shortVnd(b.chi)}</text>
                  )}
                  {i % everyX === 0 && (
                    <text x={x + slot / 2} y={H - 8} textAnchor="middle" className="lc-axis">{labelOf(b)}</text>
                  )}
                  {/* Vùng bắt chuột = cả ô, rộng hơn cột; ô CÓ giao dịch thì tab tới được. */}
                  <rect x={x} y={PAD.t} width={slot} height={PLOT_H} fill="transparent"
                    tabIndex={b.n ? 0 : undefined}
                    onMouseEnter={() => setHover(i)}
                    onFocus={() => setHover(i)}
                    onBlur={() => setHover(null)}>
                    <title>{`${labelOf(b)} · thu ${fullVnd(b.thu)} · chi ${fullVnd(b.chi)}`}</title>
                  </rect>
                </g>
              );
            })}
          </svg>

          {hb && (
            <div className="lc-tip" style={{
              left: Math.min(Math.max(PAD.l + hover * slot + slot / 2, 90), width - 90),
              top: 4,
            }}>
              <div className="lc-tip-d">{labelOf(hb)}{hb.n ? ` · ${hb.n} giao dịch` : ''}</div>
              <div className="lc-tip-r"><i style={{ background: THU }} /><b>{fullVnd(hb.thu)}</b><span>thu</span></div>
              <div className="lc-tip-r"><i style={{ background: CHI }} /><b>{fullVnd(hb.chi)}</b><span>chi</span></div>
              {hb.n > 0 && (
                <div className="lc-tip-net">Chênh lệch <b>{fullVnd(hb.thu - hb.chi)}</b></div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const LEDGER_CHART_CSS = `
.lc{padding:4px 16px 14px;position:relative}
.lc-head{display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap;margin-bottom:6px}
.lc-title{font-size:14px;font-weight:600;color:var(--text)}
.lc-sub{font-size:12px;color:var(--text3);margin-top:2px}
.lc-legend{margin-left:auto;display:flex;gap:14px;font-size:12px;color:var(--text2)}
.lc-legend span{display:inline-flex;align-items:center;gap:6px}
.lc-legend i{width:11px;height:11px;border-radius:3px;display:inline-block}
.lc-plot{position:relative}
.lc-empty{padding:28px;text-align:center;color:var(--text3);font-size:13px}
.lc-axis{font-size:10px;fill:var(--text3);font-family:inherit}
.lc-val{font-size:10.5px;font-weight:600;fill:var(--text2);font-family:inherit}
.lc rect[tabindex]:focus{outline:2px solid var(--blue);outline-offset:-2px}
.lc-tip{position:absolute;transform:translateX(-50%);background:var(--bg2);border:1px solid var(--border2);
  border-radius:10px;padding:8px 10px;box-shadow:0 10px 26px -12px rgba(0,0,0,.4);pointer-events:none;
  min-width:170px;z-index:5}
.lc-tip-d{font-size:11px;color:var(--text3);margin-bottom:5px}
.lc-tip-r{display:flex;align-items:center;gap:7px;font-size:13px;line-height:1.7}
.lc-tip-r i{width:10px;height:3px;border-radius:2px;flex:none}
.lc-tip-r b{color:var(--text);font-variant-numeric:tabular-nums}
.lc-tip-r span{color:var(--text3);font-size:11px}
.lc-tip-net{margin-top:5px;padding-top:5px;border-top:1px solid var(--border);font-size:12px;color:var(--text3)}
.lc-tip-net b{color:var(--text)}
`;
